import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Pagination, Select, Skeleton } from 'antd';
import { faBriefcase, faCircleExclamation, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import type { AgentCommissionStatus, IAgentCommission, IAgentCommissionsSummary } from '@share/common-types';
import { getAgentCommissionsAsync, getAgentCommissionsSummaryAsync } from '@share/api';
import { getUserWallet } from '@share/store/slices';
import { FunctionalRootState, RootState } from '@share/utils';

import { getAccountSectionPathById } from '../../utils';
import { AccountEmptyState } from '../empty-state';
// Same status tabs and pills as My bookings.
import filterStyles from '../bookings/styles.module.scss';

import styles from './styles.module.scss';

const PAGE_SIZE = 10;
const YEARS_BACK = 3;
const ALL_YEARS = 0;

type StatusKey = 'all' | AgentCommissionStatus;

const STATUSES: { key: StatusKey; labelId: string }[] = [
  { key: 'all', labelId: 'my.account.hub.bookings.status.all' },
  { key: 'Ready', labelId: 'agent.commissions.status.ready' },
  { key: 'Pending', labelId: 'agent.commissions.status.pending' },
  { key: 'PaidOut', labelId: 'agent.commissions.status.paid_out' },
];

const STATUS_TONE: Record<AgentCommissionStatus, string> = { Ready: 'ok', Pending: 'warning', PaidOut: 'neutral' };
const STATUS_LABEL: Record<AgentCommissionStatus, string> = {
  Ready: 'agent.commissions.status.ready',
  Pending: 'agent.commissions.status.pending',
  PaidOut: 'agent.commissions.status.paid_out',
};

interface IProps {
  accountUsername: string;
}

/**
 * The agent's commissions in My Account: what can be withdrawn now (credited to the wallet), what is
 * being credited or pending, and one row per booking. Same APIs as the commissions page (TRAV23-4670);
 * withdrawing goes through the wallet cash out.
 */
export const AccountCommissionsLedger = ({ accountUsername }: IProps): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const { userWallet, userWalletData } = useSelector((state: FunctionalRootState) => state.loginStore);
  const canCashOut = useSelector((state: FunctionalRootState) => state.loginStore.canCashOut);
  const available = userWalletData?.commissionBalance ?? 0;

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [status, setStatus] = useState<StatusKey>('all');
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState<IAgentCommissionsSummary | null>(null);
  const [rows, setRows] = useState<IAgentCommission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // The stored wallet is the one from login; reload it so "available to withdraw" is current.
  useEffect(() => {
    if (userWallet) {
      dispatch(getUserWallet(userWallet));
    }
  }, [dispatch, userWallet]);

  useEffect(() => {
    let isActive = true;
    getAgentCommissionsSummaryAsync()
      .then(response => {
        if (isActive) {
          setSummary(response.data);
        }
      })
      .catch((error: unknown) => console.error('Account hub: commissions summary failed', error));
    return () => {
      isActive = false;
    };
  }, [reloadKey]);

  const loadRows = useCallback(() => {
    let isActive = true;
    setLoading(true);
    setFailed(false);
    getAgentCommissionsAsync({
      page,
      results: PAGE_SIZE,
      year: year === ALL_YEARS ? undefined : year,
      status: status === 'all' ? undefined : status,
    })
      .then(response => {
        if (isActive) {
          setRows(response.data?.items ?? []);
          setTotal(response.data?.totalResults ?? 0);
        }
      })
      .catch((error: unknown) => {
        console.error('Account hub: commissions list failed', error);
        if (isActive) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (isActive) {
          setLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [page, year, status]);

  useEffect(() => loadRows(), [loadRows, reloadKey]);

  const currency = summary?.currency || 'USD';
  const money = (value: number | null | undefined, code = currency) =>
    intl.formatNumber(value || 0, { style: 'currency', currency: code || 'USD' });
  // Check-in and check-out are calendar dates: keep the date as sent, never shift it by time zone.
  const day = (value?: string | null) => {
    if (!value) {
      return '';
    }
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return intl.formatDate(new Date(y, (m || 1) - 1, d || 1), { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const yearOptions = useMemo(() => [
    ...Array.from({ length: YEARS_BACK }, (_, index) => ({ value: currentYear - index, label: String(currentYear - index) })),
    { value: ALL_YEARS, label: intl.formatMessage({ id: 'agent.commissions.filter.all' }) },
  ], [currentYear, intl]);

  const cashOutPath = getAccountSectionPathById(accountUsername, 'cashout');

  return (
    <div className={styles.commissions}>
      <div className={styles.cards}>
        <section className={styles.hero} aria-labelledby="account-hub-commissions-available">
          <span id="account-hub-commissions-available" className={styles.heroLabel}>
            {intl.formatMessage({ id: 'agent.commissions.withdraw.available' })}
          </span>
          <span className={styles.heroValue}>{money(available)}</span>
          <span className={styles.heroNote}>{intl.formatMessage({ id: 'agent.commissions.withdraw.hint' })}</span>
          {available > 0 && canCashOut ? (
            <Link to={cashOutPath} className={styles.heroButton}>
              {intl.formatMessage({ id: 'agent.commissions.withdraw.button' })}
            </Link>
          ) : null}
        </section>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>{intl.formatMessage({ id: 'agent.commissions.ready' })}</span>
          <span className={styles.tileValue}>{summary ? money(summary.ready) : '—'}</span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>{intl.formatMessage({ id: 'agent.commissions.pending' })}</span>
          <span className={styles.tileValue}>{summary ? money(summary.pending) : '—'}</span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>{intl.formatMessage({ id: 'agent.commissions.paid_out_year' })}</span>
          <span className={styles.tileValue}>{summary ? money(summary.paidOutThisYear) : '—'}</span>
          {summary?.paidOutBookingsThisYear ? (
            <span className={styles.tileNote}>
              {intl.formatMessage({ id: 'my.account.hub.manual.count' }, { count: summary.paidOutBookingsThisYear })}
            </span>
          ) : null}
        </div>
      </div>

      <p className={styles.note}>
        <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" />
        {intl.formatMessage({ id: 'agent.commissions.note' })}
      </p>

      <section className={styles.card} aria-labelledby="account-hub-commissions-list">
        <div className={styles.cardHeader}>
          <h2 id="account-hub-commissions-list" className={styles.cardTitle}>{intl.formatMessage({ id: 'agent.commissions.list.title' })}</h2>
          <div className={filterStyles.controls}>
            <Select<number>
              value={year}
              onChange={value => {
                setYear(value);
                setPage(1);
              }}
              className={filterStyles.sort}
              dropdownMatchSelectWidth={false}
              aria-label={intl.formatMessage({ id: 'my.account.hub.commissions.year' })}
              options={yearOptions}
            />
          </div>
        </div>

        <div role="tablist" aria-label={intl.formatMessage({ id: 'agent.commissions.table.status' })} className={filterStyles.segmented}>
          {STATUSES.map(item => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={status === item.key}
              className={status === item.key ? `${filterStyles.segment} ${filterStyles.segmentActive}` : filterStyles.segment}
              onClick={() => {
                setStatus(item.key);
                setPage(1);
              }}
            >
              {intl.formatMessage({ id: item.labelId })}
            </button>
          ))}
        </div>

        {loading && !rows.length ? <Skeleton active title={false} paragraph={{ rows: 5 }} /> : null}

        {!loading && failed ? (
          <AccountEmptyState
            icon={faCircleExclamation}
            title={intl.formatMessage({ id: 'agent.commissions.error' })}
            action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.retry' }), onClick: () => setReloadKey(key => key + 1) }}
          />
        ) : null}

        {!loading && !failed && !rows.length ? (
          <AccountEmptyState icon={faBriefcase} title={intl.formatMessage({ id: 'agent.commissions.empty' })} />
        ) : null}

        {rows.length && !failed ? (
          <div className={loading ? `${styles.tableWrap} ${styles.refreshing}` : styles.tableWrap}>
            <table className={styles.ledger}>
              <thead>
                <tr>
                  <th scope="col">{intl.formatMessage({ id: 'agent.commissions.table.booking' })}</th>
                  <th scope="col">{intl.formatMessage({ id: 'agent.commissions.table.travel_date' })}</th>
                  <th scope="col" className={styles.numeric}>{intl.formatMessage({ id: 'agent.commissions.table.total' })}</th>
                  <th scope="col">{intl.formatMessage({ id: 'agent.commissions.table.status' })}</th>
                  <th scope="col" className={styles.numeric}>{intl.formatMessage({ id: 'agent.commissions.table.commission' })}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.bookingId}>
                    <td>
                      <span className={styles.bookingName}>{row.propertyName || '—'}</span>
                      <span className={styles.bookingMeta}>{[row.leadName, row.customerLocator].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td className={styles.nowrap}>{[day(row.checkIn), day(row.checkOut)].filter(Boolean).join(' – ')}</td>
                    <td className={styles.numeric}>{money(row.bookingTotal, row.currency)}</td>
                    <td>
                      <span className={`${styles.badge} ${styles[`badge_${STATUS_TONE[row.status]}`]}`}>
                        {intl.formatMessage({ id: STATUS_LABEL[row.status] })}
                      </span>
                      {row.paidDate ? <span className={styles.bookingMeta}>{day(row.paidDate)}</span> : null}
                    </td>
                    <td className={`${styles.numeric} ${styles.amount}`}>{money(row.commission, row.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {total > PAGE_SIZE ? (
          <div className={styles.pager}>
            <Pagination current={page} pageSize={PAGE_SIZE} total={total} showSizeChanger={false} onChange={setPage} />
          </div>
        ) : null}
      </section>
    </div>
  );
};
